import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type ApiKeyCall, type ApiKeyRow, type NewApiKey } from '../../lib/api';
import type { Territory } from '../../lib/types';
import { Drawer } from '@allrouted/ui/Drawer';
import { Select } from '@allrouted/ui/Select';
import { Menu, MenuItem, KebabIcon } from '@allrouted/ui/Menu';
import { IconButton, RefreshIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';
import { Reveal, ago, errText, fullTime, until } from './AccountView';

/**
 * What integrations use to call the API.
 *
 * A key can be narrowed to one territory, made read-only, or set to expire.
 * Rolling a key issues a new secret with the same settings, and can leave the
 * old one working for a while so an integration is switched over without a
 * gap. Revoked keys stay listed, so API activity can still name them.
 */

const EXPIRY = [
  { value: 'never', label: 'Never' },
  { value: '30', label: 'In 30 days' },
  { value: '90', label: 'In 90 days' },
  { value: '180', label: 'In 6 months' },
  { value: '365', label: 'In 1 year' },
];

const GRACE = [
  { value: '0', label: 'Immediately' },
  { value: '1', label: 'After 1 hour' },
  { value: '24', label: 'After 24 hours' },
  { value: '168', label: 'After 7 days' },
];

const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.allrouted.com';

export function ApiKeysPage({ territories }: { territories: Territory[] }) {
  const ui = useUi();
  const [keys, setKeys] = useState<ApiKeyRow[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ key: NewApiKey; rolled: boolean } | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const load = useCallback(async () => {
    try {
      setKeys((await api.apiKeys()).keys);
    } catch (err) {
      ui.notify('error', 'Could not load keys', errText(err));
      setKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const live = useMemo(() => (keys ?? []).filter((k) => k.usable), [keys]);
  const dead = useMemo(() => (keys ?? []).filter((k) => !k.usable), [keys]);
  const territoryName = (id: string | null) =>
    id ? (territories.find((t) => t.id === id)?.name ?? 'One territory') : 'All territories';

  const revoke = async (k: ApiKeyRow) => {
    const ok = await ui.confirm({
      title: `Revoke “${k.label}”?`,
      body: 'Anything using this key stops working on its very next request. This cannot be undone: issue a new key instead.',
      confirmLabel: 'Revoke key',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.revokeApiKey(k.id);
      ui.notify('ok', 'Revoked', 'It stops working immediately.');
      setOpenId(null);
      await load();
    } catch (err) {
      ui.notify('error', 'Could not revoke', errText(err));
    }
  };

  const open = keys?.find((k) => k.id === openId) ?? null;

  return (
    <>
      {issued ? (
        <section className="acct-card">
          <Reveal
            title={issued.rolled ? 'Here is the new key. Copy it now: it is not shown again.' : 'Copy your new key now: it is not shown again.'}
            body="Only a digest is stored, so we cannot show it a second time. If you lose it, roll or revoke the key and issue another."
            value={issued.key.token}
            onDone={() => setIssued(null)}
          />
          <p className="hint">Try it:</p>
          <pre className="acct-snippet">{`curl ${origin}/v1/territories \\\n  -H "X-AllRouted-Key: ${issued.key.token}"`}</pre>
        </section>
      ) : null}

      <section className="acct-card">
        <div className="acct-toolbar">
          <div>
            <h2 className="acct-card-title">API keys</h2>
            <p className="hint">
              {live.length} active{dead.length ? ` · ${dead.length} revoked or expired` : ''}
            </p>
          </div>
          <span className="spacer" />
          <IconButton label="Refresh" onClick={() => void load()}>
            <RefreshIcon />
          </IconButton>
          <a className="btn" href="/docs" target="_blank" rel="noreferrer">
            API reference
          </a>
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            Create key
          </button>
        </div>

        {keys === null ? (
          <p className="hint acct-empty">Loading…</p>
        ) : live.length === 0 ? (
          <div className="acct-empty-state">
            <strong>No active keys</strong>
            <p className="hint">Create a key for each integration, so each can be revoked on its own.</p>
          </div>
        ) : (
          <KeyTable keys={live} territoryName={territoryName} onOpen={setOpenId} onRevoke={(k) => void revoke(k)} />
        )}

        {dead.length ? (
          <div className="acct-revoked">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowRevoked((v) => !v)}>
              {showRevoked ? 'Hide' : 'Show'} revoked and expired keys ({dead.length})
            </button>
            {showRevoked ? <KeyTable keys={dead} territoryName={territoryName} onOpen={setOpenId} /> : null}
          </div>
        ) : null}
      </section>

      <section className="acct-card">
        <h2 className="acct-card-title">Using a key</h2>
        <div className="acct-usage">
          <div>
            <span className="label">Send it as a header</span>
            <code>X-AllRouted-Key: &lt;your key&gt;</code>
            <span className="hint">A bearer token (Authorization: Bearer …) works too.</span>
          </div>
          <div>
            <span className="label">AllRouted API</span>
            <code>{origin}/v1</code>
          </div>
          <div>
            <span className="label">RouteManager-compatible API</span>
            <code>{origin}/api/v1</code>
            <span className="hint">An existing integration changes its base URL and key, nothing else.</span>
          </div>
        </div>
      </section>

      {creating ? (
        <CreateKeyDrawer
          territories={territories}
          onClose={() => setCreating(false)}
          onCreated={async (key) => {
            setCreating(false);
            setIssued({ key, rolled: false });
            await load();
          }}
        />
      ) : null}

      {open ? (
        <KeyDrawer
          apiKey={open}
          territories={territories}
          territoryName={territoryName}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
          onRolled={async (key) => {
            setOpenId(null);
            setIssued({ key, rolled: true });
            await load();
          }}
          onRevoke={() => void revoke(open)}
        />
      ) : null}
    </>
  );
}

function KeyTable({
  keys,
  territoryName,
  onOpen,
  onRevoke,
}: {
  keys: ApiKeyRow[];
  territoryName: (id: string | null) => string;
  onOpen: (id: string) => void;
  onRevoke?: (k: ApiKeyRow) => void;
}) {
  return (
    <div className="table-scroll">
      <table className="grid acct-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Access</th>
            <th>Territory</th>
            <th>Last used</th>
            <th>Calls, 7 days</th>
            <th>{onRevoke ? 'Expires' : 'Ended'}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} className="acct-row" onClick={() => onOpen(k.id)}>
              <td>
                <strong>{k.label}</strong>
                <span className="mono dim acct-sub">{k.token_hint}…</span>
              </td>
              <td>
                <span className="pill" data-tone={k.access === 'read' ? 'idle' : 'info'}>
                  {k.access === 'read' ? 'Read-only' : 'Full access'}
                </span>
              </td>
              <td className="dim">{territoryName(k.territory_id)}</td>
              <td className="dim" title={k.last_used_at ? `${fullTime(k.last_used_at)}${k.last_used_ip ? ` from ${k.last_used_ip}` : ''}` : undefined}>
                {ago(k.last_used_at)}
              </td>
              <td className="mono">
                {k.calls_7d.toLocaleString()}
                {k.errors_7d ? (
                  <span className="acct-errors" title="Answered with an error">
                    {' '}
                    · {k.errors_7d.toLocaleString()} errors
                  </span>
                ) : null}
              </td>
              <td className="dim" title={fullTime(k.revoked_at ?? k.expires_at)}>
                {k.revoked_at ? `Revoked ${ago(k.revoked_at).toLowerCase()}` : k.expires_at ? until(k.expires_at) : 'Never'}
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                {onRevoke ? (
                  <Menu label={`Actions for ${k.label}`} icon={<KebabIcon />} align="right">
                    <MenuItem label="Details and usage" onSelect={() => onOpen(k.id)} />
                    <MenuItem label="Revoke" danger onSelect={() => onRevoke(k)} />
                  </Menu>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccessPicker({ value, onChange }: { value: 'full' | 'read'; onChange: (v: 'full' | 'read') => void }) {
  return (
    <div className="segmented segmented-text">
      <button type="button" className="segment" aria-pressed={value === 'full'} onClick={() => onChange('full')}>
        Full access
      </button>
      <button type="button" className="segment" aria-pressed={value === 'read'} onClick={() => onChange('read')}>
        Read-only
      </button>
    </div>
  );
}

function CreateKeyDrawer({
  territories,
  onClose,
  onCreated,
}: {
  territories: Territory[];
  onClose: () => void;
  onCreated: (key: NewApiKey) => Promise<void>;
}) {
  const ui = useUi();
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState('all');
  const [access, setAccess] = useState<'full' | 'read'>('full');
  const [expiry, setExpiry] = useState('never');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!label.trim()) {
      ui.notify('info', 'Name the key', 'Say what will use it, so you can tell keys apart later.');
      return;
    }
    setBusy(true);
    try {
      const key = await api.createApiKey({
        label: label.trim(),
        territoryId: scope === 'all' ? null : scope,
        access,
        expiresInDays: expiry === 'never' ? null : Number(expiry),
      });
      await onCreated(key);
    } catch (err) {
      ui.notify('error', 'Could not create the key', errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      title="Create an API key"
      subtitle="The key is shown once, when it is created."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Creating…' : 'Create key'}
          </button>
        </>
      }
    >
      <div className="drawer-form">
        <label className="stack">
          <span className="label">Name</span>
          <input
            className="field"
            value={label}
            autoFocus
            maxLength={120}
            placeholder="What will use it? e.g. Field service integration"
            onChange={(e) => setLabel(e.target.value)}
          />
        </label>
        <div className="stack">
          <span className="label">Access</span>
          <AccessPicker value={access} onChange={setAccess} />
          <span className="hint">
            {access === 'read'
              ? 'Can read everything in its territories. Every change it attempts is refused.'
              : 'Can read and change anything in its territories, as an admin would.'}
          </span>
        </div>
        <div className="stack">
          <span className="label">Territory</span>
          <Select
            value={scope}
            options={[{ value: 'all', label: 'All territories' }, ...territories.map((t) => ({ value: t.id, label: t.name }))]}
            onChange={setScope}
          />
        </div>
        <div className="stack">
          <span className="label">Expires</span>
          <Select value={expiry} options={EXPIRY} onChange={setExpiry} />
          <span className="hint">An expiring key is good practice for anything temporary: a trial integration, a contractor.</span>
        </div>
      </div>
    </Drawer>
  );
}

function KeyDrawer({
  apiKey: k,
  territories,
  territoryName,
  onClose,
  onChanged,
  onRolled,
  onRevoke,
}: {
  apiKey: ApiKeyRow;
  territories: Territory[];
  territoryName: (id: string | null) => string;
  onClose: () => void;
  onChanged: () => void;
  onRolled: (key: NewApiKey) => Promise<void>;
  onRevoke: () => void;
}) {
  const ui = useUi();
  const [label, setLabel] = useState(k.label);
  const [scope, setScope] = useState(k.territory_id ?? 'all');
  const [access, setAccess] = useState(k.access);
  const [grace, setGrace] = useState('24');
  const [calls, setCalls] = useState<ApiKeyCall[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    api
      .apiKeyCalls(k.id)
      .then((r) => setCalls(r.calls))
      .catch(() => setCalls([]));
  }, [k.id]);

  const dirty = label.trim() !== k.label || scope !== (k.territory_id ?? 'all') || access !== k.access;

  const save = async () => {
    setBusy('save');
    try {
      await api.updateApiKey(k.id, {
        label: label.trim(),
        territoryId: scope === 'all' ? null : scope,
        access,
      });
      ui.notify('ok', 'Key updated', 'The secret is unchanged; integrations keep working.');
      onChanged();
    } catch (err) {
      ui.notify('error', 'Not saved', errText(err));
    } finally {
      setBusy(null);
    }
  };

  const roll = async () => {
    const graceHours = Number(grace);
    const ok = await ui.confirm({
      title: `Roll “${k.label}”?`,
      body:
        graceHours === 0
          ? 'A new key with the same settings is issued, and this one stops working immediately.'
          : `A new key with the same settings is issued. This one keeps working for ${GRACE.find((g) => g.value === grace)?.label.replace('After ', '')}, then stops.`,
      confirmLabel: 'Roll key',
    });
    if (!ok) return;
    setBusy('roll');
    try {
      await onRolled(await api.rollApiKey(k.id, graceHours));
    } catch (err) {
      ui.notify('error', 'Could not roll the key', errText(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Drawer
      open
      wide
      title={k.label}
      subtitle={`${k.token_hint}… · created ${ago(k.created_at).toLowerCase()}${k.created_by_email ? ` by ${k.created_by_email}` : ''}`}
      onClose={onClose}
      footer={
        k.usable ? (
          <>
            <button type="button" className="btn btn-danger" onClick={onRevoke} disabled={!!busy}>
              Revoke
            </button>
            <span className="spacer" />
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn btn-primary" disabled={!dirty || !!busy || !label.trim()} onClick={() => void save()}>
              {busy === 'save' ? 'Saving…' : 'Save changes'}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="drawer-form">
        <div className="person-facts">
          <div className="fact">
            <span className="label">Status</span>
            <span>
              {k.revoked_at ? (
                <span className="pill" data-tone="late">
                  Revoked
                </span>
              ) : !k.usable ? (
                <span className="pill" data-tone="idle">
                  Expired
                </span>
              ) : (
                <span className="pill" data-tone="ok">
                  Active
                </span>
              )}
            </span>
          </div>
          <div className="fact">
            <span className="label">Last used</span>
            <span title={fullTime(k.last_used_at)}>
              {ago(k.last_used_at)}
              {k.last_used_ip ? <span className="dim"> from {k.last_used_ip}</span> : null}
            </span>
          </div>
          <div className="fact">
            <span className="label">Calls, 24 hours</span>
            <span className="mono">{k.calls_24h.toLocaleString()}</span>
          </div>
          <div className="fact">
            <span className="label">Calls, 7 days</span>
            <span className="mono">
              {k.calls_7d.toLocaleString()}
              {k.errors_7d ? <span className="acct-errors"> · {k.errors_7d} errors</span> : null}
            </span>
          </div>
          <div className="fact">
            <span className="label">{k.revoked_at ? 'Revoked' : 'Expires'}</span>
            <span title={fullTime(k.revoked_at ?? k.expires_at)}>
              {k.revoked_at
                ? `${ago(k.revoked_at)}${k.revoked_by_email ? ` by ${k.revoked_by_email}` : ''}`
                : k.expires_at
                  ? until(k.expires_at)
                  : 'Never'}
            </span>
          </div>
          {!k.usable ? (
            <div className="fact">
              <span className="label">Reached</span>
              <span>
                {territoryName(k.territory_id)} · {k.access === 'read' ? 'read-only' : 'full access'}
              </span>
            </div>
          ) : null}
        </div>

        {k.usable ? (
          <>
            <label className="stack">
              <span className="label">Name</span>
              <input className="field" value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <div className="acct-two">
              <div className="stack">
                <span className="label">Access</span>
                <AccessPicker value={access} onChange={setAccess} />
              </div>
              <div className="stack">
                <span className="label">Territory</span>
                <Select
                  value={scope}
                  options={[{ value: 'all', label: 'All territories' }, ...territories.map((t) => ({ value: t.id, label: t.name }))]}
                  onChange={setScope}
                />
              </div>
            </div>

            <div className="acct-roll">
              <div>
                <strong>Roll this key</strong>
                <p className="hint">Issue a new secret with the same settings, for routine rotation or if this one may have leaked.</p>
              </div>
              <div className="acct-roll-controls">
                <span className="label">Old key stops</span>
                <Select value={grace} options={GRACE} onChange={setGrace} />
                <button type="button" className="btn" disabled={!!busy} onClick={() => void roll()}>
                  {busy === 'roll' ? 'Rolling…' : 'Roll key'}
                </button>
              </div>
            </div>
          </>
        ) : null}

        <div className="stack">
          <span className="label">Recent calls</span>
          {calls === null ? (
            <p className="hint">Loading…</p>
          ) : calls.length === 0 ? (
            <p className="hint">No calls recorded with this key yet.</p>
          ) : (
            <div className="table-scroll">
              <table className="grid acct-calls">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Request</th>
                    <th>Status</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => (
                    <tr key={c.id}>
                      <td className="dim" title={fullTime(c.at)}>
                        {ago(c.at)}
                      </td>
                      <td className="mono acct-path">
                        <span className="acct-method">{c.method}</span> {c.path}
                      </td>
                      <td>
                        <span className="pill" data-tone={c.status >= 500 ? 'late' : c.status >= 400 ? 'warn' : 'ok'}>
                          {c.status}
                        </span>
                      </td>
                      <td className="mono dim">{c.duration_ms} ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <span className="hint">Full request and response bodies are under API activity in the account menu.</span>
        </div>
      </div>
    </Drawer>
  );
}
